import { cn } from "@-/com.npmjs.cn";
import { Platform, View } from "@-/com.npmjs.react-native";
import {
  Skeleton,
  SkeletonAccordionSection,
  SkeletonGroup,
  SkeletonTileGrid,
} from "@-/dev.improve.haniverse.components.ui.skeleton";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { usePrimaryControlsStripOpaqueChrome } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import type { ReactNode } from "react";

// These placeholders intentionally have no data, navigation, or theme-provider dependencies:
// the fallback must remain renderable when any of those providers is still loading. Each one
// mirrors its loaded screen's padding, corners and control sizes using the shared skeleton blocks.
function LoadingBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <SkeletonGroup
      className={cn("min-h-0 flex-1 overflow-hidden bg-background", className)}
      testID="layout-skeleton"
    >
      {children}
    </SkeletonGroup>
  );
}

/** A `leading-7` paragraph: `text-base` bars on a 28px rhythm, ending short. */
function Paragraph({ lines = 4 }: { lines?: number }) {
  return (
    <View className="mt-3 gap-3">
      {Array.from({ length: lines }, (_, line) => (
        <Skeleton className={cn("h-4", line === lines - 1 ? "w-2/3" : "w-full")} key={line} />
      ))}
    </View>
  );
}

function Separator({ className }: { className?: string }) {
  return <View className={cn("h-px bg-border", className)} />;
}

const SETTINGS_GROUPS = [
  {
    rows: ["value", "value", "switch", "switch", "switch", "chevron"],
    title: "w-16",
  },
  { rows: ["switch", "switch", "switch", "switch"], title: "w-24" },
] as const;

const SETTINGS_LABEL_WIDTHS = ["w-16", "w-24", "w-28", "w-32", "w-20", "w-24"];

/** `SettingsRowGroup`s: uppercase caption, flush `rounded-xs` card of icon rows, footnote. */
export function SettingsLayoutSkeleton() {
  return (
    <LoadingBody className="pt-3">
      {SETTINGS_GROUPS.map((group, groupIndex) => (
        <View className="px-2.25" key={groupIndex}>
          <Skeleton className={cn("mt-3.5 mb-1.5 h-3", group.title)} />
          <View className="overflow-hidden rounded-xs bg-surface">
            {group.rows.map((trailing, row) => (
              <View key={row}>
                {row > 0 && <Separator />}
                <View className="flex-row items-center py-2">
                  <Skeleton className="mr-3 size-7" />
                  <Skeleton
                    className={cn("h-4", SETTINGS_LABEL_WIDTHS[row % SETTINGS_LABEL_WIDTHS.length])}
                  />
                  <View className="flex-1" />
                  {trailing === "switch" ? (
                    <Skeleton className="h-4.5 w-8 rounded-full" />
                  ) : (
                    <View className="flex-row items-center gap-1">
                      {trailing === "value" && <Skeleton className="h-4 w-14" />}
                      <Skeleton className="size-5" />
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
          <Skeleton className="mx-1 mt-2.5 h-3 w-3/4" />
        </View>
      ))}
    </LoadingBody>
  );
}

/** Default sign-in: centred heading, full-width provider pills, email link and password toggle. */
export function SignInLayoutSkeleton() {
  return (
    <LoadingBody className="items-center justify-center px-6 py-3">
      <View className="w-full gap-5">
        <Skeleton className="h-7 w-40 self-center" />
        <View className="w-full gap-3">
          {Array.from({ length: Platform.OS === "ios" ? 2 : 1 }, (_, key) => (
            <Skeleton className="h-12 w-full rounded-full" key={key} />
          ))}
          <View className="h-11 items-center justify-center">
            <Skeleton className="h-3 w-36" />
          </View>
        </View>
        <View className="h-10 items-center justify-center">
          <Skeleton className="h-3 w-32" />
        </View>
      </View>
    </LoadingBody>
  );
}

/**
 * `AuthForm` (compact) and the account forms: optional title/description, `h-12 rounded-xs`
 * inputs and a matching submit button.
 */
export function FormLayoutSkeleton({
  fields = 2,
  heading = true,
}: {
  fields?: number;
  /** Account forms omit the title, description and field labels. */
  heading?: boolean;
}) {
  return (
    <LoadingBody className="px-5 pt-6">
      <View className="mx-auto w-full max-w-md gap-8">
        {heading && (
          <View className="gap-4">
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-3.5 w-5/6" />
          </View>
        )}
        <View className={heading ? "gap-3" : "gap-4 px-1"}>
          {Array.from({ length: fields }, (_, key) => (
            <View className="gap-1" key={key}>
              {heading && <Skeleton className="my-0.5 h-3.5 w-20" />}
              <Skeleton className="h-12 w-full rounded-xs" />
            </View>
          ))}
        </View>
        <Skeleton className="h-12 w-full rounded-xs" />
      </View>
    </LoadingBody>
  );
}

/** Legal documents: `text-4xl` title, date, intro, then `text-2xl` sections of prose. */
export function DocumentLayoutSkeleton() {
  return (
    <LoadingBody className="gap-6 px-5 py-6">
      <View className="gap-3">
        <Skeleton className="h-9 w-3/4" />
        <Skeleton className="h-3.5 w-32" />
        <Paragraph lines={3} />
      </View>
      {[0, 1, 2].map((key) => (
        <View className="gap-3" key={key}>
          <Skeleton className="h-6 w-1/2" />
          <Paragraph />
        </View>
      ))}
    </LoadingBody>
  );
}

/** Deck list: flat 56px rows with a drag handle, collection icon, name and overflow button. */
export function DeckLayoutSkeleton() {
  return (
    <LoadingBody className="py-2">
      {[0, 1, 2, 3, 4, 5].map((key) => (
        <View className="h-14 flex-row items-center gap-2 border-b border-border px-4" key={key}>
          <View className="w-6 items-center">
            <Skeleton className="size-5" />
          </View>
          <Skeleton className="size-5" />
          <Skeleton className={cn("h-4", key % 2 === 0 ? "w-32" : "w-24")} />
          <View className="flex-1" />
          <View className="size-8 items-center justify-center">
            <Skeleton className="size-4" />
          </View>
        </View>
      ))}
    </LoadingBody>
  );
}

/** Deck collection: an eight-column grid of glyph tiles with 12px outer padding. */
export function DeckCollectionLayoutSkeleton() {
  return (
    <LoadingBody className="px-2 pt-2">
      <SkeletonTileGrid rows={6} />
    </LoadingBody>
  );
}

/** Search: the in-flow pill toolbar above the idle results prompt. */
export function SearchLayoutSkeleton() {
  const { diameter, height, leadingInset } = useToolbarGridMetrics();
  return (
    <LoadingBody className="bg-muted">
      <View
        className="flex-row items-center gap-0.5 bg-muted"
        style={{ height, paddingLeft: leadingInset }}
      >
        {[
          { id: "search", width: "w-24" },
          { id: "filter", width: "w-20" },
          { id: "sort", width: "w-20" },
        ].map(({ id, width }) => (
          <Skeleton className={cn("rounded-full", width)} key={id} style={{ height: diameter }} />
        ))}
      </View>
      <View className="flex-1 items-center justify-center px-6">
        <Skeleton className="h-4 w-48" />
      </View>
    </LoadingBody>
  );
}

/** Review/lesson card: bordered `rounded-xs` face with the glyph tile and heading lines. */
export function StudyLayoutSkeleton() {
  return (
    <LoadingBody className="bg-transparent">
      <View className="flex-1 overflow-hidden rounded-xs border border-border bg-surface px-2.25 pt-10 pb-14">
        {/* Sized in CSS rather than from the window width, which the web server renders as 0. */}
        <View className="flex-2 items-center justify-center px-6">
          <Skeleton className="aspect-square w-full max-w-70" />
        </View>
        <View className="shrink-0 items-center gap-4 pt-2">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-4 w-48" />
        </View>
      </View>
    </LoadingBody>
  );
}

/** Character details load the flat summary contents, without placeholder sheet chrome. */
export function ReferenceLayoutSkeleton() {
  return <MorphemeSummarySkeleton />;
}

/** Compact word summary: characters and pinyin beside definitions, then the occurrences row. */
export function WordLayoutSkeleton() {
  usePrimaryControlsStripOpaqueChrome(true);
  return (
    <LoadingBody className="bg-transparent">
      <View className="flex-row items-start gap-3 px-3 py-2">
        <View className="w-2/5 items-center gap-2">
          <View className="flex-row gap-1">
            <Skeleton className="size-7" />
            <Skeleton className="size-7" />
          </View>
          <Skeleton className="h-3.5 w-16" />
        </View>
        <View className="min-w-0 flex-1 gap-2 pt-1">
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-3/4" />
        </View>
      </View>
      <View className="mx-3 flex-row items-center gap-2 rounded-xs border border-border/60 px-3 py-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-24" />
      </View>
    </LoadingBody>
  );
}

/** Gloss/decomposition disambiguation: one intro line above a four-column glyph grid. */
export function DisambiguateLayoutSkeleton() {
  return (
    <LoadingBody>
      <View className="px-4 pt-2 pb-1">
        <Skeleton className="h-3.5 w-3/4" />
      </View>
      <SkeletonTileGrid className="gap-2 p-2" columns={4} rows={3} />
    </LoadingBody>
  );
}

/** Calligraphy: the Strokes surface (title and count header, a row of stroke steps, Replay). */
export function CalligraphyLayoutSkeleton() {
  return (
    <LoadingBody className="p-2.25">
      <View className="overflow-hidden rounded-xs border border-border/60 bg-surface">
        <View className="flex-row items-center justify-between border-b border-border/60 bg-surface-header px-3 py-2">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-3 w-16" />
        </View>
        <View className="items-center gap-3 p-3">
          <View className="w-full flex-row gap-3">
            {[0, 1, 2].map((key) => (
              <Skeleton className="aspect-square flex-1 rounded-xs" key={key} />
            ))}
          </View>
          <Skeleton className="mt-1 h-9 w-20 rounded-xs" />
        </View>
      </View>
    </LoadingBody>
  );
}

/** Sentences accordion: example rows with a wrapped sentence and round action buttons. */
export function SentencesLayoutSkeleton() {
  return (
    <LoadingBody className="bg-surface">
      <SkeletonAccordionSection titleClassName="w-20">
        {[0, 1, 2, 3].map((key) => (
          <View className={cn("gap-y-2 px-4 py-3", key > 0 && "border-t border-border")} key={key}>
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
            <View className="flex-row items-center gap-x-2">
              {[0, 1, 2].map((button) => (
                <Skeleton className="size-7 rounded-full" key={button} />
              ))}
            </View>
          </View>
        ))}
      </SkeletonAccordionSection>
    </LoadingBody>
  );
}

/** Progress accordion: state/percent header, caption and stat rows, then the other sections. */
export function ProgressLayoutSkeleton() {
  return (
    <LoadingBody className="bg-surface">
      <SkeletonAccordionSection titleClassName="w-20">
        <View className="gap-2 px-4 pt-2 pb-4">
          <View className="flex-row items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-10" />
          </View>
          <Skeleton className="h-3 w-40" />
          <View className="mt-2 gap-2.5">
            {[0, 1, 2, 3, 4, 5].map((key) => (
              <View className="flex-row items-center justify-between" key={key}>
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3.5 w-12" />
              </View>
            ))}
          </View>
        </View>
      </SkeletonAccordionSection>
      <SkeletonAccordionSection titleClassName="w-32" />
      <SkeletonAccordionSection titleClassName="w-16" />
    </LoadingBody>
  );
}

/** Meaning summary: centred glyph, gloss and aliases, sentence, then the component row. */
export function MnemonicLayoutSkeleton() {
  return <MorphemeSummarySkeleton />;
}

function MorphemeSummarySkeleton() {
  // Content placeholders stay crisp at the top, even beneath a mounted word/card toolbar.
  usePrimaryControlsStripOpaqueChrome(true);
  return (
    <LoadingBody className="bg-transparent">
      {/* Only the summary contents load; no placeholder sheet, shadow or drag handle. */}
      <View className="items-center gap-1 px-3 pt-2.25 pb-2">
        <Skeleton className="size-20" />
        <View className="w-full items-center gap-2 py-1">
          <Skeleton className="h-6.5 w-32" />
          <View className="w-full flex-row justify-center gap-2">
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/5" />
            <Skeleton className="h-6 w-1/4" />
          </View>
        </View>
      </View>
      <View className="px-3 pt-2 pb-3">
        <Skeleton className="h-4 w-5/6" />
      </View>
      <View className="mx-1 flex-row items-center rounded-xs border border-border/60 px-3 py-2">
        {[0, 1].map((key) => (
          <View className="min-w-0 flex-1 flex-row items-center gap-2" key={key}>
            <Skeleton className="size-8" />
            <Skeleton className="h-3 w-12" />
          </View>
        ))}
        <Skeleton className="size-5" />
      </View>
    </LoadingBody>
  );
}
